/**
 * [BL] Featured reviews carousel.
 *
 * Ported from bylumine_hydrogen (KeyReviewsCarousel): fade between review slides,
 * autoplay (default 5s), clickable dots, and touch/mouse swipe.
 *
 * Autoplay pauses on hover and while focus is inside, has a pause / play button beside the dots
 * (WCAG 2.2.2), and is off for visitors who ask for reduced motion.
 *
 * Markup contract (rendered by sections/featured-reviews.liquid):
 *   <reviews-carousel autoplay="5">
 *     <div class="bl-reviews__viewport">
 *       <div class="bl-reviews__track" data-track>
 *         <div class="bl-reviews__slide" data-review-slide>…</div>
 *         …
 *       </div>
 *     </div>
 *     <div class="bl-reviews__dots" data-dots></div>
 *   </reviews-carousel>
 */

const SWIPE_THRESHOLD = 30;

class ReviewsCarousel extends HTMLElement {
  #index = 0;
  #interval;
  #slides = [];
  /** Dots, swipe and element listeners are set up once (the editor can reconnect the element). */
  #ready = false;
  /** Paused with the pause button: stays paused until played again. */
  #stopped = false;

  connectedCallback() {
    if (!this.#ready) {
      this.track = this.querySelector('[data-track]');
      this.dotsContainer = this.querySelector('[data-dots]');
      this.#slides = Array.from(this.querySelectorAll('[data-review-slide]'));
      if (this.#slides.length === 0) return;
      this.#ready = true;

      this.#buildDots();
      this.#show(0);

      if (this.#slides.length > 1) {
        this.#bindSwipe();
        this.addEventListener('mouseenter', this.#pause);
        this.addEventListener('mouseleave', this.#resume);
        this.addEventListener('focusin', this.#pause);
        this.addEventListener('focusout', (event) => {
          if (!this.contains(/** @type {Node | null} */ (event.relatedTarget))) this.#resume();
        });
      }
    }

    if (this.#slides.length > 1) {
      document.addEventListener('visibilitychange', this.#handleVisibility);
      this.#resume();
    }
  }

  disconnectedCallback() {
    clearInterval(this.#interval);
    document.removeEventListener('visibilitychange', this.#handleVisibility);
  }

  get autoplayMs() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
    const value = parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isNaN(value) ? 0 : value * 1000;
  }

  #buildDots() {
    if (!this.dotsContainer || this.#slides.length <= 1) return;

    this.dots = this.#slides.map((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'bl-reviews__dot';
      dot.setAttribute('aria-label', `Go to review ${index + 1}`);
      dot.addEventListener('click', () => {
        this.#show(index);
        this.#resume();
      });
      this.dotsContainer.appendChild(dot);
      return dot;
    });

    if (this.autoplayMs > 0) {
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'bl-reviews__toggle';
      toggle.innerHTML =
        '<svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false"><path class="bl-reviews__toggle-pause" d="M3.5 2v8M8.5 2v8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path class="bl-reviews__toggle-play" d="M3.5 2l6 4-6 4z" fill="currentColor"/></svg>';
      const label = () => {
        toggle.setAttribute('aria-label', this.#stopped ? 'Play reviews' : 'Pause reviews');
        toggle.toggleAttribute('data-stopped', this.#stopped);
      };
      label();
      toggle.addEventListener('click', () => {
        this.#stopped = !this.#stopped;
        label();
        if (this.#stopped) this.#pause();
        else this.#resume();
      });
      this.dotsContainer.appendChild(toggle);
    }
  }

  #show(index) {
    const count = this.#slides.length;
    this.#index = ((index % count) + count) % count;

    this.#slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    if (this.dots) {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.#index);
        if (i === this.#index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
  }

  #next = () => this.#show(this.#index + 1);
  #prev = () => this.#show(this.#index - 1);

  #resume = () => {
    clearInterval(this.#interval);
    if (this.#stopped || this.contains(document.activeElement)) return;
    if (this.autoplayMs > 0 && this.#slides.length > 1) {
      this.#interval = setInterval(() => {
        if (!document.hidden && !this.matches(':hover')) this.#next();
      }, this.autoplayMs);
    }
  };

  #pause = () => clearInterval(this.#interval);

  #handleVisibility = () => (document.hidden ? this.#pause() : this.#resume());

  #bindSwipe() {
    let startX = null;

    const start = (x) => {
      startX = x;
      this.classList.add('is-grabbing');
    };
    const end = (x) => {
      if (startX == null) return;
      const delta = x - startX;
      if (Math.abs(delta) > SWIPE_THRESHOLD) {
        delta < 0 ? this.#next() : this.#prev();
        this.#resume();
      }
      startX = null;
      this.classList.remove('is-grabbing');
    };

    this.addEventListener('touchstart', (e) => start(e.touches[0].clientX), { passive: true });
    this.addEventListener('touchend', (e) => end(e.changedTouches[0].clientX), { passive: true });

    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      start(e.clientX);
    });
    this.addEventListener('pointerup', (e) => end(e.clientX));
    this.addEventListener('pointercancel', () => {
      startX = null;
      this.classList.remove('is-grabbing');
    });
    // Prevent text selection / image drag while dragging.
    this.addEventListener('dragstart', (e) => e.preventDefault());
  }
}

if (!customElements.get('reviews-carousel')) {
  customElements.define('reviews-carousel', ReviewsCarousel);
}
